import { dateToString } from './dateToString'
import { formatYesNoToTrueFalse } from './formatter'
import { reduceObject } from './reduceObject'
import { subjectType } from '../../strings/types'
import { validateInteger } from './validateInteger'
import { business_categories } from 'hotline_complaint_choices'

// Return the awards that have the given type
export const formatAwards = (awards, gcType) => {
  return awards.filter(award => award.award_type === gcType)
}

export const formatEmploymentData = data =>
  reduceObject(
    [
      'address',
      'email',
      'employer',
      'job_title',
      'phone',
      'phone2',
      'work_place'
    ],
    data,
    'employment__{}'
  )

export const formatAddressData = address => ({
  address: address.line1Value,
  address2: address.line2Value,
  ...reduceObject(['city', 'state', 'zip', 'country'], address, '{}Value')
})

export const formatPersonData = (person, keyFormat) => {
  const data = reduceObject(
    [
      'first_name',
      'middle_name',
      'last_name',
      'suffix',
      'alias',
      'category',
      'email',
      'hhs_employee',
      'hhs_program_number',
      'phone',
      'phone2',
      'sex',
      'ssn'
    ],
    person,
    keyFormat
  )

  const age = data.age ? { age: validateInteger(parseInt(data.age)) } : {}
  const dob = data.dob ? { dob: dateToString(data.dob) } : {}

  return {
    ...age,
    ...data,
    ...dob
  }
}

export const formatBusinessData = (business, keyFormat) => {
  const data = reduceObject(
    ['business_name', 'business_category', 'dba', 'ein', 'npi', 'website', 'phone', 'phone2'],
    business,
    keyFormat
  )

  return {
    ...data
  }
}

export const formatSubjects = subjects =>
  subjects.map(sub =>
    sub.subject_type === subjectType.business
      ? formatSubjectBusiness(sub)
      : formatSubjectIndividual(sub)
  )

export const formatSubjectBusiness = subject => {
  const { description, subject_type, ...rest } = subject
  const businessData = formatBusinessData(rest)
  const employmentData = formatEmploymentData(rest)

  return {
    ...businessData,
    ...formatAddressData(subject.address),
    description,
    employment: {
      ...employmentData,
      ...formatAddressData(employmentData.address)
    },
    subject_type
  }
}

export const formatSubjectIndividual = subject => {
  const { description, subject_type, ...rest } = subject
  const personData = formatPersonData(rest)
  const employmentData = formatEmploymentData(rest)

  return {
    ...personData,
    ...formatAddressData(subject.address),
    description,
    employment: {
      ...employmentData,
      ...formatAddressData(employmentData.address)
    },
    subject_type
  }
}

/**
 * Legacy formatting methods
 */

/*
 * Convert the grant/contract form data into a list of objects.
 * gcType dictates either Grant or Contract.
 */
export const formDataForRequestAwards = (formData, gcType) => {
  const awards = []
  for (let id = 0; id <= 9; id++) {
    const award_type = formData[`award_type_${id}`]
    if (award_type === gcType) {
      const award_number = formData[`award_number_${id}`]
      const executive_director = formData[`executive_director_${id}`]
      awards.push({
        award_number: award_number,
        executive_director: executive_director
      })
    }
  }
  return awards
}

export const formDataForRequestComplainant = formData => {
  // Return complainant data key:value for the request sent to the API
  const {
    non_anon_perm_to_disclose,
    perm_to_disclose,
    complainant_first_name,
    complainant_middle_name,
    complainant_last_name,
    complainant_suffix,
    complainant_personal_email,
    complainant_home_phone,
    complainant_cell_phone,
    complainant_work_phone,
    complainant_address,
    complainant_dob,
    complainant_employer,
    complainant_employer_phone,
    complainant_medicare_number,
    complainant_medicaid_number,
    complainant_job_title,
    complainant_ssn,
    complainant_hhs_employee,
    complainant_work_address,
    complainant_work_email,
    work_place,
    anonymous_acknowledgement
  } = formData

  let complainant = {}
  if (
    non_anon_perm_to_disclose ||
    (perm_to_disclose && perm_to_disclose.toUpperCase() !== 'ANONYMOUS')
  ) {
    complainant = {
      first_name: complainant_first_name,
      middle_name: complainant_middle_name,
      last_name: complainant_last_name,
      suffix: complainant_suffix,
      dob: dateToString(complainant_dob),
      email: complainant_personal_email,
      phone: complainant_home_phone,
      phone2: complainant_cell_phone,
      address: complainant_address.line1Value,
      address2: complainant_address.line2Value,
      city: complainant_address.cityValue,
      state: complainant_address.stateValue,
      zip: complainant_address.zipValue,
      country: complainant_address.countryValue,
      ssn: complainant_ssn,
      hhs_employee: formatYesNoToTrueFalse(complainant_hhs_employee),
      medicare_number: complainant_medicare_number,
      medicaid_number: complainant_medicaid_number,
      employment: {
        employer: complainant_employer,
        address:
          complainant_work_address && complainant_work_address.line1Value,
        address2:
          complainant_work_address && complainant_work_address.line2Value,
        city: complainant_work_address && complainant_work_address.cityValue,
        state: complainant_work_address && complainant_work_address.stateValue,
        zip: complainant_work_address && complainant_work_address.zipValue,
        country:
          complainant_work_address && complainant_work_address.countryValue,
        job_title: complainant_job_title,
        email: complainant_work_email,
        phone: complainant_employer_phone,
        phone2: complainant_work_phone,
        work_place: work_place
      }
    }
  }
  if (anonymous_acknowledgement) {
    complainant.anonymous_acknowledgement = formatYesNoToTrueFalse(
      anonymous_acknowledgement[0]
    )
  }

  return complainant
}

export const formDataForRequestSubjects = formData => {
  // Return an array of subject data key:value for the request sent to the API
  const {
    subj_relationship,
    subj_relationship_other,
    subj_address,
    subj_business_name,
    subj_business_category,
    subj_business_dba,
    subj_business_employer_id,
    subj_business_npi,
    subj_business_address,
    additional_identifying_information,
    subj_website,
    subj_first_name,
    subj_middle_name,
    subj_last_name,
    subj_suffix,
    subj_individual_category,
    subj_alternate_phone,
    subj_business_phone,
    subj_cell_phone,
    subj_home_phone,
    subj_work_phone,
    subj_personal_email,
    subj_work_email,
    subj_employer,
    subj_employer_address,
    subj_employer_phone,
    subj_job_title,
    subj_alias_nickname,
    subj_physical_description,
    subj_dob
  } = formData

  const subjects = []
  if (subj_business_name) {
    subjects.push({
      business_name: subj_business_name,
      business_category: subj_business_category,
      dba: subj_business_dba,
      ein: subj_business_employer_id,
      npi: subj_business_npi,
      phone: subj_business_phone,
      phone2: subj_alternate_phone,
      website: subj_website,
      address: subj_business_address.line1Value,
      address2: subj_business_address.line2Value,
      city: subj_business_address.cityValue,
      state: subj_business_address.stateValue,
      zip: subj_business_address.zipValue,
      country: subj_business_address.countryValue,
      description: additional_identifying_information
    })
  } else if (subj_first_name) {
    subjects.push({
      first_name: subj_first_name,
      middle_name: subj_middle_name,
      last_name: subj_last_name,
      suffix: subj_suffix,
      category: subj_individual_category,
      email: subj_personal_email,
      dob: dateToString(subj_dob),
      description: subj_physical_description,
      phone: subj_cell_phone,
      phone2: subj_home_phone,
      address: subj_address.line1Value,
      address2: subj_address.line2Value,
      city: subj_address.cityValue,
      state: subj_address.stateValue,
      zip: subj_address.zipValue,
      country: subj_address.countryValue,
      relationship: subj_relationship,
      relationship_other: subj_relationship_other,
      alias: subj_alias_nickname,
      employment: {
        employer: subj_employer,
        job_title: subj_job_title,
        address: subj_employer_address.line1Value,
        address2: subj_employer_address.line2Value,
        city: subj_employer_address.cityValue,
        state: subj_employer_address.stateValue,
        zip: subj_employer_address.zipValue,
        country: subj_employer_address.countryValue,
        phone: subj_employer_phone,
        phone2: subj_work_phone,
        email: subj_work_email
      }
    })
  }
  return subjects
}

export const formDataForRequestVictim = formData => {
  // Return victim data key:value for the request sent to the API
  const {
    victim_first_name,
    victim_middle_name,
    victim_last_name,
    victim_suffix,
    victim_alias_nickname,
    victim_address,
    victim_personal_phone,
    victim_alternative_phone,
    victim_email,
    victim_dob,
    victim_ssn,
    victim_medicare_number,
    victim_medicaid_number
  } = formData

  let victim = undefined
  if (victim_first_name) {
    victim = {
      first_name: victim_first_name,
      middle_name: victim_middle_name,
      last_name: victim_last_name,
      suffix: victim_suffix,
      alias: victim_alias_nickname,
      dob: dateToString(victim_dob),
      email: victim_email,
      phone: victim_personal_phone,
      phone2: victim_alternative_phone,
      address: victim_address.line1Value,
      address2: victim_address.line2Value,
      city: victim_address.cityValue,
      state: victim_address.stateValue,
      zip: victim_address.zipValue,
      country: victim_address.countryValue,
      ssn: victim_ssn,
      medicare_number: victim_medicare_number,
      medicaid_number: victim_medicaid_number
    }
  }
  return victim
}

export const formDataForRequestWitnesses = formData => {
  // Return an array of witness data key:value for the request sent to the API
  const {
    witness_first_name,
    witness_middle_name,
    witness_last_name,
    witness_suffix,
    witness_alias_nickname,
    witness_dob,
    witness_email,
    witness_work_email,
    witness_home_phone,
    witness_cell_phone,
    witness_work_phone,
    witness_address,
    witness_job_title,
    witness_employer,
    witness_employer_phone,
    witness_employer_address
  } = formData

  const witnesses = []
  if (witness_first_name) {
    witnesses.push({
      first_name: witness_first_name,
      middle_name: witness_middle_name,
      last_name: witness_last_name,
      suffix: witness_suffix,
      alias: witness_alias_nickname,
      dob: dateToString(witness_dob),
      email: witness_email,
      phone: witness_home_phone,
      phone2: witness_cell_phone,
      address: witness_address.line1Value,
      address2: witness_address.line2Value,
      city: witness_address.cityValue,
      state: witness_address.stateValue,
      zip: witness_address.zipValue,
      country: witness_address.countryValue,
      employment: {
        employer: witness_employer,
        job_title: witness_job_title,
        address: witness_employer_address.line1Value,
        address2: witness_employer_address.line2Value,
        city: witness_employer_address.cityValue,
        state: witness_employer_address.stateValue,
        zip: witness_employer_address.zipValue,
        country: witness_employer_address.countryValue,
        email: witness_work_email,
        phone: witness_employer_phone,
        phone2: witness_work_phone
      }
    })
  }
  return witnesses
}
